/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import CampaignModel from 'cio/models/campaign';
import { Periods } from 'cio/utils/metrics/common';
import {
  JourneyMetrics,
  JourneyMetricsMeasure,
  AVAILABLE_MEASURES,
  MEASURE_COLORS,
  JOURNEY_MEASURE_DEFINITIONS,
  DATA_CAMPAIGN_JOURNEY_MEASURE_DEFINITIONS,
  MEASURE_LABELS,
} from 'cio/utils/journey-metrics';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import MetricsService from 'cio/services/metrics';
import { Grouping } from 'cio/utils/metrics/resolution';
import { DataContainer, prepareDataContainer, SidebarItem } from 'cio/utils/campaign-overview/metrics';
import { findDenominator, MeasureType } from 'cio/utils/metrics/measure';
import AnalyticsService from 'cio/services/analytics';
import SettingsService from 'cio/services/settings';

interface Args {
  campaign: CampaignModel;
  timeRange: DateRange;
  timePeriod: Periods;
  grouping: Grouping;
  unit: 'count' | 'percentage';
  journeysMetrics: JourneyMetrics;
  measures: JourneyMetricsMeasure[];
  onMeasuresChange: (measures: JourneyMetricsMeasure[], trackEvent?: boolean) => unknown;
  isLoadingMetrics: boolean;
  onTrackCopyLink: () => unknown;
}

export default class JourneysMetricsTab extends Component<Args> {
  @service analytics!: AnalyticsService;
  @service metrics!: MetricsService;
  @service settings!: SettingsService;
  @service environment!: EnvironmentService;

  @action
  onSelectSidebarItem(itemId: JourneyMetricsMeasure, checked: boolean) {
    if (checked) {
      this.args.onMeasuresChange([...this.args.measures, itemId]);
    } else {
      this.args.onMeasuresChange(this.args.measures.filter((m) => m !== itemId));
    }
  }

  @tracked chartData: DataContainer<JourneyMetricsMeasure> = {} as any;
  @tracked isLoading = true;

  get sidebarItems(): SidebarItem<JourneyMetricsMeasure>[] {
    let journeyMeasureDefinitions = this.args.campaign.isDataCampaign
      ? DATA_CAMPAIGN_JOURNEY_MEASURE_DEFINITIONS
      : JOURNEY_MEASURE_DEFINITIONS;

    let availableMeasures = [...AVAILABLE_MEASURES];

    if (this.args.campaign.isDataCampaign) {
      availableMeasures = availableMeasures.filter((m) => m !== 'converted');
    }

    return availableMeasures.map((measure: JourneyMetricsMeasure, i: number) => {
      let label = (MEASURE_LABELS as Record<string, string>)[measure];
      let denominatorMeasure = findDenominator(measure, MeasureType.JourneyMetrics);
      let hasRate = denominatorMeasure != null;

      return {
        id: measure,
        value: measure,
        index: i,
        label,
        color: MEASURE_COLORS[measure],
        hasRate,
        disabled: !hasRate && this.args.unit === 'percentage',
        disabledTooltip: `Select "Count" to view ${label} data`,
        measure,
        denominatorMeasure,
        definition: journeyMeasureDefinitions[measure],
      };
    });
  }

  @action
  setDataContainer() {
    if (this.args.isLoadingMetrics || this.args.journeysMetrics == null) {
      return;
    }

    this.chartData = prepareDataContainer(
      this.args.journeysMetrics,
      this.sidebarItems.map((m) => m.measure),
    );

    if (this.isLoading) {
      this.isLoading = false;
    }
  }

  @action
  onTrackHover(time: number) {
    this.analytics.track('campaign', 'journey_metrics_chart_hover', {
      model: this.args.campaign,
      time,
      selectedMetrics: this.args.measures,
      measureType: MeasureType.JourneyMetrics,
      metricType: 'measure',
    });
  }
}
